<template>
  <div class="container mx-auto px-8 py-6 sm:py-7 md:py-8 lg:py-10">
    <div class="w-full py-12 text-slate-900">
      <Transition name="view-slide" mode="out-in">
        <GroupsList
          v-if="lg.currentView.value === 'groups'"
          key="groups"
          :groups="lg.groups.value"
          :member-group-ids="lg.memberGroupIds.value"
          :joining-group-id="lg.joiningGroupId.value"
          :join-group-error="lg.joinGroupError.value"
          :search-query="lg.searchQuery.value"
          :show-disease-dropdown="lg.showDiseaseDropdown.value"
          :selected-disease="lg.selectedDisease.value"
          :filtered-diseases="lg.filteredDiseases.value"
          :filtered-groups="lg.filteredGroups.value"
          @view-group="lg.viewGroup"
          @create-group="lg.handleCreateGroup"
          @join-group="lg.handleJoinGroup"
          @leave-group="lg.handleLeaveGroup"
          @update:search-query="
            (v) => {
              lg.searchQuery.value = v;
              lg.filterDiseases();
            }
          "
          @update:show-disease-dropdown="
            (v) => {
              lg.showDiseaseDropdown.value = v;
            }
          "
          @select-disease="lg.selectDisease"
          @clear-disease="lg.clearDisease"
          @search-blur="lg.onSearchBlur"
        />

        <GroupDetail
          v-else-if="lg.activeDetailGroup.value"
          key="detail"
          :group="lg.activeDetailGroup.value"
          :sessions="lg.sessions.value"
          :loading-sessions="lg.loadingSessions.value"
          :expanded-session-id="lg.expandedSessionId.value"
          :member-group-ids="lg.memberGroupIds.value"
          :is-logged-in="props.isLoggedIn"
          :session-link-copied="lg.sessionLinkCopied.value"
          :detail-link-copied="lg.detailLinkCopied.value"
          @back="
            props.backUrl
              ? router.push(props.backUrl)
              : (lg.currentView.value = 'groups')
          "
          @propose-session="lg.handleProposeSession"
          @toggle-session="lg.toggleSessionAttendance"
          @expand-session="
            (id) => {
              lg.expandedSessionId.value =
                lg.expandedSessionId.value === id ? null : id;
            }
          "
          @share-session="(session) => lg.openSharePopup('session', session)"
          @share-detail="() => lg.openSharePopup('group')"
          @whatsapp-detail="lg.shareDetailGroupWhatsApp"
          @telegram-detail="lg.shareDetailGroupTelegram"
          @copy-detail="lg.copyDetailGroupLink"
        />
      </Transition>
    </div>
  </div>

  <CreateGroupModal
    v-if="lg.modalMode.value === 'create'"
    :is-open="lg.showModal.value"
    :current-step="lg.currentStep.value"
    :is-submitting="lg.isSubmitting.value"
    :submit-error="lg.submitError.value"
    :is-private="lg.isPrivate.value"
    :modal-disease-search="lg.modalDiseaseSearch.value"
    :modal-selected-disease="lg.modalSelectedDisease.value"
    :filtered-modal-diseases="lg.filteredModalDiseases.value"
    :group-name="lg.groupName.value"
    :group-description="lg.groupDescription.value"
    :success-title="lg.successTitle.value"
    :success-message="lg.successMessage.value"
    :success-mode="lg.successMode.value"
    :created-group="lg.createdGroup.value"
    :created-group-slug="lg.createdGroupSlug.value"
    :group-created-copied="lg.groupCreatedCopied.value"
    :link-copied="lg.linkCopied.value"
    @close="lg.closeModal"
    @choose-type="lg.chooseGroupType"
    @filter-modal-diseases="
      (v) => {
        lg.modalDiseaseSearch.value = v;
        lg.filterModalDiseases();
      }
    "
    @select-modal-disease="
      (d) => {
        lg.modalSelectedDisease.value = d;
      }
    "
    @update:group-name="
      (v) => {
        lg.groupName.value = v;
      }
    "
    @update:group-description="
      (v) => {
        lg.groupDescription.value = v;
      }
    "
    @update:modal-disease-search="
      (v) => {
        lg.modalDiseaseSearch.value = v;
      }
    "
    @update:current-step="
      (v) => {
        lg.currentStep.value = v;
      }
    "
    @submit="lg.handleSubmitCreate"
    @go-to-group="lg.goToCreatedGroup"
    @share-whatsapp="lg.shareNewGroupWhatsApp"
    @share-telegram="lg.shareNewGroupTelegram"
    @copy-invite="lg.copyNewGroupInvite"
  />

  <ProposeSessionModal
    v-if="lg.modalMode.value === 'propose-session'"
    :is-open="lg.showModal.value"
    :current-step="lg.currentStep.value"
    :group-name="lg.activeDetailGroup.value?.name ?? ''"
    :proposal-date="lg.proposalDate.value"
    :proposal-days="lg.proposalDays.value"
    :available-time-slots="lg.availableTimeSlots.value"
    :proposal-time="lg.proposalTime.value"
    :proposal-description="lg.proposalDescription.value"
    :proposal-max-attendees="lg.proposalMaxAttendees.value"
    :is-submitting="lg.isSubmitting.value"
    :submit-error="lg.submitError.value"
    :success-title="lg.successTitle.value"
    :success-message="lg.successMessage.value"
    :success-mode="lg.successMode.value"
    :session-info-copied="lg.sessionInfoCopied.value"
    @close="lg.closeModal"
    @select-day="lg.selectProposalDay"
    @update:proposal-time="
      (v) => {
        lg.proposalTime.value = v;
      }
    "
    @update:proposal-description="
      (v) => {
        lg.proposalDescription.value = v;
      }
    "
    @update:proposal-max-attendees="
      (v) => {
        lg.proposalMaxAttendees.value = v ?? '';
      }
    "
    @submit="lg.handleSubmitSession"
    @share-whatsapp="lg.shareSessionWhatsApp"
    @share-telegram="lg.shareSessionTelegram"
    @copy-info="lg.copySessionInfo"
  />

  <SharePopup
    :is-open="lg.sharePopupOpen.value"
    :url="lg.sharePopupUrl.value"
    :whatsapp-url="lg.sharePopupWhatsAppUrl.value"
    :telegram-url="lg.sharePopupTelegramUrl.value"
    :copied="lg.sharePopupCopied.value"
    @close="lg.sharePopupOpen.value = false"
    @copy="lg.sharePopupCopy"
  />

  <JoinSuccessPopup
    :is-open="lg.joinSuccessPopupOpen.value"
    @close="lg.closeJoinSuccess"
    @share="lg.handleJoinSuccessShare"
  />

  <LeaveConfirmDialog
    :is-open="lg.showLeaveConfirm.value"
    :group="lg.groupToLeave.value"
    :is-leaving-group="lg.isLeavingGroup.value"
    @confirm="lg.confirmLeaveGroup"
    @cancel="lg.showLeaveConfirm.value = false"
  />

  <GuestJoinPopup
    :is-open="
      !!lg.guestJoinPopupSession.value || !!lg.guestJoinPopupGroup.value
    "
    :guest-join-state="lg.guestJoinState.value"
    :guest-join-email="lg.guestJoinEmail.value"
    :guest-join-loading="lg.guestJoinLoading.value"
    :guest-join-error="lg.guestJoinError.value"
    @close="lg.closeGuestJoinPopup"
    @login="lg.guestJoinChooseLogin"
    @register="lg.guestJoinChooseRegister"
    @update:guest-join-email="
      (v) => {
        lg.guestJoinEmail.value = v;
      }
    "
    @update:guest-join-state="
      (v) => {
        lg.guestJoinState.value = v;
      }
    "
    @submit-email="lg.guestJoinSubmitEmail"
  />
</template>

<script setup lang="ts">
import type { Group } from "@/types/domain";
import { useLiveGroups } from "@/components/companionGroups/useLiveGroups";
import GroupsList from "@/components/companionGroups/GroupsList.vue";
import GroupDetail from "@/components/companionGroups/GroupDetail.vue";
import CreateGroupModal from "@/components/companionGroups/CreateGroupModal.vue";
import ProposeSessionModal from "@/components/companionGroups/ProposeSessionModal.vue";
import SharePopup from "@/components/companionGroups/SharePopup.vue";
import LeaveConfirmDialog from "@/components/companionGroups/LeaveConfirmDialog.vue";
import GuestJoinPopup from "@/components/companionGroups/GuestJoinPopup.vue";
import JoinSuccessPopup from "@/components/companionGroups/JoinSuccessPopup.vue";

const props = defineProps<{
  isLoggedIn?: boolean;
  /** Auto-navigate to the public group matching this disease key on mount */
  initialDiseaseKey?: string;
  /** Auto-navigate to the private group matching this slug on mount */
  initialGroupSlug?: string;
  /** If set, the back button navigates to this URL instead of showing the internal groups list */
  backUrl?: string;
}>();

const emit = defineEmits<{
  (e: "group-created", group: Group): void;
  (e: "group-joined", group: Group): void;
  (e: "group-left", group: Group): void;
}>();

const lg = useLiveGroups(props, emit);
const { router } = lg;
</script>

<style scoped>
.view-slide-enter-active,
.view-slide-leave-active {
  transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
}
.view-slide-enter-from {
  opacity: 0;
  transform: translateX(28px);
}
.view-slide-leave-to {
  opacity: 0;
  transform: translateX(-28px);
}
</style>
